import HyperDX from "@hyperdx/browser";

const DEFAULT_SERVICE_NAME = "tarifix-helm";

let initialized = false;
let interactionListenersStarted = false;
let lastPageView: string | undefined;

const env = {
  advancedNetworkCapture: process.env.NEXT_PUBLIC_HYPERDX_ADVANCED_NETWORK_CAPTURE,
  apiKey: process.env.NEXT_PUBLIC_HYPERDX_API_KEY || process.env.NEXT_PUBLIC_HDX_API_KEY,
  consoleCapture: process.env.NEXT_PUBLIC_HYPERDX_CONSOLE_CAPTURE,
  disableReplay: process.env.NEXT_PUBLIC_HYPERDX_DISABLE_REPLAY,
  disabled: process.env.NEXT_PUBLIC_HYPERDX_DISABLED,
  serviceName: process.env.NEXT_PUBLIC_HYPERDX_SERVICE_NAME,
  telemetryUrl: process.env.NEXT_PUBLIC_HYPERDX_URL,
  apiUrl: process.env.NEXT_PUBLIC_API_URL,
};

function cleanEnv(value: string | undefined): string | undefined {
  return value && value.trim().length > 0 ? value : undefined;
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function getTracePropagationTargets(): RegExp[] {
  const apiUrl = cleanEnv(env.apiUrl);

  if (!apiUrl) {
    return [];
  }

  try {
    return [new RegExp(escapeRegex(new URL(apiUrl).origin), "i")];
  } catch {
    return [];
  }
}

function getCurrentPath(): string {
  if (typeof window === "undefined") {
    return "";
  }

  return `${window.location.pathname}${window.location.search}`;
}

function getActionName(element: Element, fallback: string): string {
  return (
    element.getAttribute("data-hdx-action") ||
    element.getAttribute("data-analytics-action") ||
    fallback
  );
}

function getElementAttributes(element: Element): Record<string, string> {
  const attributes: Record<string, string> = {
    "ui.element.tag": element.tagName.toLowerCase(),
    "ui.page.path": getCurrentPath(),
  };

  const role = element.getAttribute("role");
  const action = element.getAttribute("data-hdx-action") || element.getAttribute("data-analytics-action");
  const type = element.getAttribute("type");

  if (role) {
    attributes["ui.element.role"] = role;
  }

  if (action) {
    attributes["ui.action"] = action;
  }

  if (type) {
    attributes["ui.element.type"] = type;
  }

  if (element instanceof HTMLAnchorElement) {
    try {
      const href = new URL(element.href);
      attributes["ui.link.path"] = `${href.pathname}${href.search}`;
    } catch {
      // Ignore malformed or non-URL hrefs.
    }
  }

  return attributes;
}

function findInteractiveElement(target: EventTarget | null): Element | null {
  if (!(target instanceof Element)) {
    return null;
  }

  return target.closest(
    "a,button,input[type='button'],input[type='submit'],[role='button'],[data-hdx-action],[data-analytics-action]",
  );
}

function startInteractionTracking(): void {
  if (interactionListenersStarted || typeof document === "undefined") {
    return;
  }

  document.addEventListener(
    "click",
    (event) => {
      const element = findInteractiveElement(event.target);

      if (!element) {
        return;
      }

      HyperDX.addAction(getActionName(element, "UI Clicked"), {
        ...getElementAttributes(element),
        "ui.interaction.type": "click",
      });
    },
    true,
  );

  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target instanceof HTMLFormElement ? event.target : null;

      if (!form) {
        return;
      }

      const attributes = getElementAttributes(form);
      attributes["ui.interaction.type"] = "submit";
      attributes["ui.form.method"] = form.method.toLowerCase();

      try {
        const action = new URL(form.action);
        attributes["ui.form.action_path"] = `${action.pathname}${action.search}`;
      } catch {
        // Ignore malformed form actions.
      }

      HyperDX.addAction(getActionName(form, "Form Submitted"), attributes);
    },
    true,
  );

  interactionListenersStarted = true;
}

export function initHyperDXBrowserTelemetry(): boolean {
  if (initialized || typeof window === "undefined") {
    return initialized;
  }

  if (cleanEnv(env.disabled) === "true") {
    return false;
  }

  const apiKey = cleanEnv(env.apiKey);

  if (!apiKey) {
    return false;
  }

  HyperDX.init({
    apiKey,
    service: cleanEnv(env.serviceName) || DEFAULT_SERVICE_NAME,
    url: cleanEnv(env.telemetryUrl),
    consoleCapture: cleanEnv(env.consoleCapture) === "true",
    advancedNetworkCapture: cleanEnv(env.advancedNetworkCapture) === "true",
    disableReplay: cleanEnv(env.disableReplay) === "true",
    maskAllInputs: true,
    tracePropagationTargets: getTracePropagationTargets(),
  });

  initialized = true;
  startInteractionTracking();

  return true;
}

export function recordHyperDXPageView(path: string = getCurrentPath()): void {
  if (!initHyperDXBrowserTelemetry() || !path || path === lastPageView) {
    return;
  }

  lastPageView = path;
  HyperDX.addAction("Page Viewed", {
    "ui.page.path": path,
    "ui.interaction.type": "navigation",
  });
}
